import { createContext, useCallback, useMemo, useRef, useState, type ReactNode } from "react";
import { Toaster } from "@/components/ui/Toaster";

export interface ToastItem {
  id: string;
  message: string;
  variant: "success" | "error" | "info";
}

export interface ToastContextValue {
  toasts: ToastItem[];
  addToast: (message: string, variant: ToastItem["variant"]) => void;
  removeToast: (id: string) => void;
}

const MAX_VISIBLE = 3;
const AUTO_DISMISS_MS = 5000;

export const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const removeToast = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const addToast = useCallback(
    (message: string, variant: ToastItem["variant"]) => {
      const id = crypto.randomUUID();
      const toast: ToastItem = { id, message, variant };

      setToasts((prev) => {
        const next = [...prev, toast];
        // Evict oldest if over limit
        if (next.length > MAX_VISIBLE) {
          const evicted = next.slice(0, next.length - MAX_VISIBLE);
          for (const t of evicted) {
            const timer = timersRef.current.get(t.id);
            if (timer) {
              clearTimeout(timer);
              timersRef.current.delete(t.id);
            }
          }
          return next.slice(-MAX_VISIBLE);
        }
        return next;
      });

      // Auto-dismiss
      const timer = setTimeout(() => {
        timersRef.current.delete(id);
        setToasts((prev) => prev.filter((t) => t.id !== id));
      }, AUTO_DISMISS_MS);
      timersRef.current.set(id, timer);
    },
    [],
  );

  const value = useMemo<ToastContextValue>(
    () => ({ toasts, addToast, removeToast }),
    [toasts, addToast, removeToast],
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster toasts={toasts} onDismiss={removeToast} />
    </ToastContext.Provider>
  );
}
